import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Send, Volume2, VolumeX, Settings2 } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Avatar from "@/components/Avatar";
import VoiceButton from "@/components/VoiceButton";
import ChatBubble from "@/components/ChatBubble";
import CrisisCard from "@/components/CrisisCard";
import VoicePicker from "@/components/VoicePicker";
import Onboarding from "@/components/Onboarding";
import StreakBadge from "@/components/StreakBadge";
import ShareCard from "@/components/ShareCard";
import ReportDialog from "@/components/ReportDialog";
import { trackEvent } from "@/lib/analytics";

const BREATH_MS = 4000;

export default function Companion() {
    const { conversationId: routeConv } = useParams();
    const navigate = useNavigate();
    const { user, setUser } = useAuth();
    const [conversationId, setConversationId] = useState(routeConv || null);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [busy, setBusy] = useState(false);
    const [avatarState, setAvatarState] = useState("idle");
    const [voiceOn, setVoiceOn] = useState(true);
    const [crisisVisible, setCrisisVisible] = useState(false);
    const [voicePickerOpen, setVoicePickerOpen] = useState(false);
    const [emotion, setEmotion] = useState("attentive");
    const [reportingId, setReportingId] = useState(null);
    const audioRef = useRef(null);
    const scrollRef = useRef(null);

    // Greeting / daily check-in
    useEffect(() => {
        if (routeConv) {
            api.get(`/conversations/${routeConv}/messages`)
                .then(r => setMessages(r.data))
                .catch(() => toast.error("Couldn't load that conversation"));
            setConversationId(routeConv);
            return;
        }
        // No conversation — fetch a personalised daily check-in greeting
        api.get("/checkin")
            .then(r => {
                setMessages([{ id: "intro", role: "assistant", content: r.data.greeting }]);
            })
            .catch(() => {
                setMessages([{
                    id: "intro", role: "assistant",
                    content: "Hi. I'm Pax.\n\nThere's no agenda here — we can just talk. How are you arriving today?",
                }]);
            });
    }, [routeConv]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages]);

    const speak = async (text) => {
        if (!voiceOn) return;
        try {
            const token = localStorage.getItem("lumen_token");
            const res = await fetch(`${API}/tts`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ text }),
            });
            if (res.ok) {
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                if (!audioRef.current) audioRef.current = new Audio();
                const audio = audioRef.current;
                audio.src = url;
                audio.crossOrigin = "anonymous";
                setAvatarState("speaking");
                audio.onended = () => { setAvatarState("idle"); URL.revokeObjectURL(url); };
                audio.onerror = () => { setAvatarState("idle"); URL.revokeObjectURL(url); };
                await audio.play();
                return;
            }
            speakWithBrowser(text);
        } catch {
            speakWithBrowser(text);
        }
    };

    const speakWithBrowser = (text) => {
        if (!("speechSynthesis" in window)) { setAvatarState("idle"); return; }
        try {
            window.speechSynthesis.cancel();
            const u = new SpeechSynthesisUtterance(text);
            u.rate = 0.82 * (user?.voice_speed || 1);
            u.pitch = 1.0;
            const lang = user?.locale === "hi" ? "hi-IN" : "en-IN";
            u.lang = lang;
            const voices = window.speechSynthesis.getVoices();
            const preferred = voices.find(v => v.lang === lang) || voices.find(v => /samantha|female|google us english|karen|moira/i.test(v.name));
            if (preferred) u.voice = preferred;
            setAvatarState("speaking");
            u.onend = () => setAvatarState("idle");
            u.onerror = () => setAvatarState("idle");
            window.speechSynthesis.speak(u);
        } catch {
            setAvatarState("idle");
        }
    };

    const send = async (text) => {
        const t = (text ?? draft).trim();
        if (!t || busy) return;
        setBusy(true);
        setAvatarState("thinking");
        const userMsg = { id: `u-${Date.now()}`, role: "user", content: t };
        const placeholderId = `a-${Date.now()}`;
        setMessages(prev => [
            ...prev.filter(m => m.id !== "intro"),
            userMsg,
            { id: placeholderId, role: "assistant", content: "" },
        ]);
        setDraft("");

        try {
            const token = localStorage.getItem("lumen_token");
            trackEvent("message_sent", { message_length: t.length });
            const res = await fetch(`${API}/chat/stream`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ message: t, conversation_id: conversationId }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || "pax couldn't respond");
            }

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let fullText = "";
            let newConvId = null;
            let messageId = null;
            let breathStart = null;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n");
                buffer = lines.pop();
                for (const line of lines) {
                    if (!line.startsWith("data: ")) continue;
                    try {
                        const evt = JSON.parse(line.slice(6));
                        if (evt.type === "meta") {
                            newConvId = evt.conversation_id;
                            if (evt.crisis_detected) setCrisisVisible(true);
                            if (evt.pace === "slow") { breathStart = Date.now(); setAvatarState("breathing"); }
                        } else if (evt.type === "delta") {
                            fullText += evt.content;
                            setMessages(prev => prev.map(m =>
                                m.id === placeholderId ? { ...m, content: fullText } : m
                            ));
                        } else if (evt.type === "done") {
                            messageId = evt.message_id;
                            if (evt.emotion) setEmotion(evt.emotion);
                        } else if (evt.type === "error") {
                            const friendly = evt.code === "budget_exceeded"
                                ? "pax needs a moment — please try again in a bit."
                                : "pax couldn't reach the right words just now.";
                            throw new Error(friendly);
                        }
                    } catch (e) {
                        if (e.message?.includes("pax")) throw e;
                    }
                }
            }

            // Replace placeholder id with real message id
            if (messageId) {
                setMessages(prev => prev.map(m => m.id === placeholderId ? { ...m, id: messageId } : m));
            }
            if (newConvId && !conversationId) {
                setConversationId(newConvId);
                navigate(`/companion/${newConvId}`, { replace: true });
            }
            if (breathStart) {
                const remaining = BREATH_MS - (Date.now() - breathStart);
                if (remaining > 0) await new Promise(r => setTimeout(r, remaining));
            }
            if (fullText) await speak(fullText);
            else setAvatarState("idle");
        } catch (err) {
            toast.error(err.message || "pax couldn't respond");
            // remove the empty placeholder
            setMessages(prev => prev.filter(m => m.id !== placeholderId));
            setAvatarState("idle");
        } finally {
            setBusy(false);
        }
    };

    const handleVoiceTranscript = (text) => {
        setAvatarState("listening");
        send(text);
    };

    const newConversation = () => {
        setConversationId(null);
        setCrisisVisible(false);
        api.get("/checkin")
            .then(r => setMessages([{ id: "intro", role: "assistant", content: r.data.greeting }]))
            .catch(() => setMessages([{ id: "intro", role: "assistant", content: "A fresh start. What's on your mind?" }]));
        navigate("/companion");
    };

    return (
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-14 items-start">
            <Onboarding />
            {/* Avatar column */}
            <div className="flex flex-col items-center pt-2 md:pt-6 lg:sticky lg:top-6">
                <Avatar state={avatarState} emotion={emotion} size={280} audioElement={audioRef.current} testId="companion-avatar" />
                <div className="mt-3">
                    <StreakBadge />
                </div>
                <div className="mt-8 md:mt-12 flex items-center gap-4">
                    <VoiceButton onTranscript={handleVoiceTranscript} disabled={busy} />
                    <button
                        data-testid="toggle-voice-btn"
                        onClick={() => { setVoiceOn(v => !v); trackEvent("voice_toggled"); }}
                        className="w-12 h-12 rounded-full bg-clay hover:bg-clayHover flex items-center justify-center text-ink transition-colors"
                        aria-label={voiceOn ? "Mute pax" : "Unmute pax"}
                    >
                        {voiceOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
                    </button>
                    <button
                        data-testid="open-voice-picker"
                        onClick={() => setVoicePickerOpen(true)}
                        className="w-12 h-12 rounded-full bg-clay hover:bg-clayHover flex items-center justify-center text-ink transition-colors"
                        aria-label="Choose voice"
                    >
                        <Settings2 className="w-5 h-5" />
                    </button>
                </div>
                <button
                    data-testid="new-conversation-btn"
                    onClick={newConversation}
                    className="mt-6 md:mt-8 text-sm text-inkSoft hover:text-terracotta font-body"
                >
                    + start fresh
                </button>
                <div className="hidden lg:block w-full mt-8">
                    <ShareCard />
                </div>
            </div>

            {/* Chat column */}
            <div className="bg-white/70 border border-clayHover rounded-3xl p-4 sm:p-6 md:p-8 shadow-soft min-h-[50vh] md:min-h-[60vh] flex flex-col">
                <CrisisCard visible={crisisVisible} onDismiss={() => setCrisisVisible(false)} />
                <div ref={scrollRef} className="flex-1 overflow-y-auto pr-1 mb-4 max-h-[50vh] md:max-h-[55vh]" data-testid="messages-list">
                    {messages.map(m => (
                        <ChatBubble
                            key={m.id}
                            role={m.role}
                            content={m.content || (m.role === "assistant" && busy ? "…" : "")}
                            testId={`msg-${m.role}`}
                            onReport={
                                m.role === "assistant" && m.id !== "intro" && !String(m.id).startsWith("a-")
                                    ? () => setReportingId(m.id)
                                    : undefined
                            }
                        />
                    ))}
                </div>
                <form
                    onSubmit={(e) => { e.preventDefault(); send(); }}
                    className="flex items-end gap-3 border-t border-clayHover pt-4"
                >
                    <textarea
                        data-testid="chat-input"
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                        }}
                        rows={1}
                        placeholder="say anything…"
                        className="flex-1 bg-transparent resize-none outline-none text-ink font-body placeholder:text-inkSoft/60 max-h-32"
                    />
                    <button
                        data-testid="send-btn"
                        type="submit"
                        disabled={busy || !draft.trim()}
                        className="w-11 h-11 rounded-full bg-terracotta text-bone flex items-center justify-center hover:bg-terracottaHover transition-colors disabled:opacity-40"
                        aria-label="Send"
                    >
                        <Send className="w-4 h-4" />
                    </button>
                </form>
            </div>

            <div className="lg:hidden col-span-1">
                <ShareCard />
            </div>

            <VoicePicker
                open={voicePickerOpen}
                onClose={() => setVoicePickerOpen(false)}
                currentVoice={user?.voice}
                currentLocale={user?.locale || "en"}
                currentSpeed={user?.voice_speed || 1}
                onVoiceChange={(u) => setUser(u)}
            />
            {reportingId && (
                <ReportDialog messageId={reportingId} onClose={() => setReportingId(null)} />
            )}
        </div>
    );
}
